<script setup lang="ts">
	import {
		MapLibrePolyline,
		MapLibrePolygon,
		MapLibreLabel,
		MapLibreMachineMarker,
		useMap,
	} from "@ptx-autonomous/libs/maplibre";
	import { ref, computed, nextTick } from "vue";
	import bbox from "@turf/bbox";
	import type { ImplementV2Model } from "@ptx-autonomous/libs/flightpath-service-api-models";
	import type {
		Feature,
		Polygon,
		MultiPolygon,
		LineString,
		MultiLineString,
		Position,
	} from "geojson";
	import PathResultsPanel from "@/components/PathResultsPanel.vue";
	import machineSvg from "@/assets/machine.svg?raw";
	import pointerAutoSvg from "@/assets/pointer-auto.svg?raw";
	import { advancedBuffer } from "@shared/utils";
	import type {
		SingleMachineField,
		PlanPathResults,
		PlanPathResult,
	} from "@shared/types";
	import {
		DEFAULT_TARGET_SPEED,
		DEFAULT_TURNING_SPEED,
		DEFAULT_APPROACH_SPEED,
		fieldFill,
		fieldStroke,
		estimatedCoverageFill,
		estimatedCoverageStroke,
		pathStroke,
		pathSymbol,
		shortenedSegmentFill,
		shortenedSegmentStroke,
		drivenPathStroke,
		coverageFill,
		coverageStroke,
	} from "@shared/state";
	import PinIcon from "@/icons/PinIcon.vue";
	import FinishIcon from "@/icons/FinishIcon.vue";
	import InitialPrompt from "@/components/omnibar/InitialPrompt.vue";
	import SelectImplement from "@/components/omnibar/SelectImplement.vue";
	import SelectField from "@/components/omnibar/SelectField.vue";
	import PathPlanningOptions from "@/components/omnibar/PathPlanningOptions.vue";
	import PathsGenerated from "@/components/omnibar/PathsGenerated.vue";
	import UserMenu from "@/components/UserMenu.vue";
	import SettingsDialog from "@/components/SettingsDialog.vue";
	import { useFlightpathServiceHttp } from "@ptx-autonomous/libs/flightpath-service-api";
	import {
		useFields,
		useImplementsV2,
		optionsFromImplementModel,
	} from "@shared/composables";
	import type { PreviewMarker } from "@shared/types";

	const { map } = useMap();
	const fieldListContainer = ref<HTMLDivElement>();

	const http = useFlightpathServiceHttp();
	const allowNonRtkRecordedBoundaries = ref(true);
	const { data: allFields, isLoading: isLoadingFields } = useFields(
		http,
		allowNonRtkRecordedBoundaries,
	);
	const { data: allImplements, isLoading: isLoadingImplements } =
		useImplementsV2(http);

	const isSettingsDialogOpen = ref(false);
	const generatedPath = ref<LineString | null>(null);
	const planPathResults = ref<PlanPathResults>([]);
	const selectedPathIndex = ref(0);
	const selectedField = ref<SingleMachineField>();
	const selectedImplement = ref<ImplementV2Model>();

	const missionPreviewCoverage = ref<Polygon | MultiPolygon | null>(null);
	const missionPreviewDrivenPath = ref<MultiLineString | null>(null);
	const missionPreviewMarker = ref<PreviewMarker | null>(null);

	const pathPlanningOptions = ref({
		heading: 0,
		overlap: 0, // in inches
		prefersConsecutiveOrder: false,
		startLocation: undefined as Position | undefined,
		targetSpeed: DEFAULT_TARGET_SPEED,
		turningSpeed: DEFAULT_TURNING_SPEED,
		approachSpeed: DEFAULT_APPROACH_SPEED,
		uncapHeadlands: true,
	});

	const omnibarStep = ref<
		| "initial"
		| "selectImplement"
		| "selectField"
		| "pathPlanning"
		| "pathGenerated"
		| "selectStartLocation"
		| "selectAbLine"
	>("initial");
	const isOmnibarExpanded = ref(false);
	const isCreatingField = ref(false);

	function startPlanning() {
		if (omnibarStep.value === "initial") {
			omnibarStep.value = "selectImplement";
			isOmnibarExpanded.value = true;
		}
	}

	function selectImplementFromOmnibar(implement: ImplementV2Model) {
		selectedImplement.value = implement;

		const implementOptions = optionsFromImplementModel(implement);
		if (implementOptions) {
			pathPlanningOptions.value.overlap = implementOptions.overlap; // Already in inches
		}

		omnibarStep.value = "selectField";
		generatedPath.value = null;
	}

	function handleFieldClick(field: SingleMachineField) {
		if (omnibarStep.value === "selectField") {
			if (
				selectedField.value &&
				selectedField.value.fieldId === field.fieldId
			) {
				// Second click on same field: confirm selection and go to path planning
				omnibarStep.value = "pathPlanning";
				flyToField(field);
			} else {
				selectedField.value = field;
				flyToField(field, false);
			}
		}
	}

	async function scrollToSelectedField() {
		await nextTick();
		if (!selectedField.value || !fieldListContainer.value) return;

		const selectedFieldElement = fieldListContainer.value.querySelector(
			`[data-field-id="${selectedField.value.fieldId}"]`,
		);
		selectedFieldElement?.scrollIntoView({
			block: "center",
			inline: "start",
		});
	}

	function handlePathsGenerated(results: PlanPathResults) {
		planPathResults.value = results;
		selectedPathIndex.value = 0;

		if (results.length > 0) {
			generatedPath.value = results[0].pathResult?.path || null;
			omnibarStep.value = "pathGenerated";
			isOmnibarExpanded.value = true;
		} else {
			alert("No paths generated. Please adjust your parameters.");
			resetOmnibar();
		}
	}

	function resetOmnibar() {
		omnibarStep.value = "initial";
		isOmnibarExpanded.value = false;
		selectedField.value = undefined;
		selectedImplement.value = undefined;
		generatedPath.value = null;
		planPathResults.value = [];
		selectedPathIndex.value = 0;
		isCreatingField.value = false;

		// Reset mission preview state
		missionPreviewCoverage.value = null;
		missionPreviewDrivenPath.value = null;
		missionPreviewMarker.value = null;

		// Reset path planning options state
		pathPlanningOptions.value = {
			heading: 0,
			overlap: 0, // in inches
			prefersConsecutiveOrder: false,
			startLocation: undefined as Position | undefined,
			targetSpeed: DEFAULT_TARGET_SPEED,
			turningSpeed: DEFAULT_TURNING_SPEED,
			approachSpeed: DEFAULT_APPROACH_SPEED,
			uncapHeadlands: true,
		};
	}

	function handlePathChange(result: PlanPathResult, index: number) {
		selectedPathIndex.value = index;

		if (result.pathResult && result.pathResult.path) {
			generatedPath.value = result.pathResult.path;
		}
	}

	function handleModifyPath() {
		// Clear all path-related data but keep field boundary
		generatedPath.value = null;
		planPathResults.value = [];
		selectedPathIndex.value = 0;
		missionPreviewCoverage.value = null;
		missionPreviewDrivenPath.value = null;
		missionPreviewMarker.value = null;

		// Reset path planning progress and return to planning step
		omnibarStep.value = "pathPlanning";
	}

	function handleSavePath() {
		const selectedResult = planPathResults.value[selectedPathIndex.value];
		if (selectedResult) {
			alert(`You are now ready to start your mission`);
		}
	}

	function handlePlanningOptionsUpdate(
		options: typeof pathPlanningOptions.value,
	) {
		pathPlanningOptions.value = { ...options };
	}

	function handleMissionPreviewUpdate(data: {
		coverage: Polygon | MultiPolygon | null;
		drivenPath: MultiLineString;
		previewMarker: PreviewMarker;
	}) {
		missionPreviewCoverage.value = data.coverage;
		missionPreviewDrivenPath.value = data.drivenPath;
		missionPreviewMarker.value = data.previewMarker;
	}

	const currentEstimatedCoverage = computed(() => {
		if (planPathResults.value.length === 0 || selectedPathIndex.value < 0) {
			return null;
		}
		const currentResult = planPathResults.value[selectedPathIndex.value];
		return currentResult?.pathResult?.estimatedCoverage || null;
	});

	const currentFieldBoundary = computed(() => {
		return selectedField.value?.fieldBoundary || null;
	});

	const currentShortenedSegments = computed<Polygon[]>(() => {
		if (planPathResults.value.length === 0 || selectedPathIndex.value < 0) {
			return [];
		}
		const currentResult = planPathResults.value[selectedPathIndex.value];
		const mitigationData = currentResult?.pathResult?.mitigationData;

		if (!mitigationData?.shortenedSegments || !selectedImplement.value)
			return [];

		const implementWidth =
			selectedImplement.value.Sections?.[0]?.ElementWorkingWidth || 0;

		return mitigationData.shortenedSegments
			.map(segment => {
				try {
					const buffered = advancedBuffer(
						{
							type: "Feature",
							properties: {},
							geometry: {
								type: "LineString",
								coordinates: [
									[segment.Start.Long, segment.Start.Lat],
									[segment.End.Long, segment.End.Lat],
								],
							},
						},
						{
							distance: implementWidth / 2,
							units: "meters",
							endCapStyle: "flat",
						},
					);

					if (!buffered?.geometry?.coordinates) return null;

					return {
						type: "Polygon",
						coordinates: buffered.geometry.coordinates,
					} as Polygon;
				} catch (error) {
					console.error(
						"Error buffering shortened segment:",
						error,
						segment,
					);
					return null;
				}
			})
			.filter((polygon): polygon is Polygon => polygon !== null);
	});

	const currentStartPoint = computed<Position | undefined>(() => {
		if (planPathResults.value.length === 0 || selectedPathIndex.value < 0) {
			return undefined;
		}
		const currentResult = planPathResults.value[selectedPathIndex.value];
		const path = currentResult?.pathResult?.path;
		return path?.coordinates?.[0];
	});

	const currentEndPoint = computed<Position | undefined>(() => {
		if (planPathResults.value.length === 0 || selectedPathIndex.value < 0) {
			return undefined;
		}
		const currentResult = planPathResults.value[selectedPathIndex.value];
		const path = currentResult?.pathResult?.path;
		return path?.coordinates?.[path.coordinates.length - 1];
	});

	function flyToField(field: SingleMachineField, accountForOmnibar = true) {
		if (!map || !field.fieldBoundary) return;

		const fieldFeature: Feature<Polygon> = {
			type: "Feature",
			geometry: field.fieldBoundary,
			id: field.fieldId,
			properties: {},
		};

		const height = window.innerHeight;
		const bounds = bbox(fieldFeature) as [number, number, number, number];
		if (!accountForOmnibar) {
			map?.fitBounds(bounds, {
				padding: {
					top: height / 12,
					bottom: height / 12,
					left: 460,
					right: 40,
				},
				duration: 200,
				animate: true,
			});
		} else {
			map?.fitBounds(bounds, {
				padding: {
					top: height / 12,
					bottom: 240,
					left: 460,
					right: 40,
				},
				duration: 200,
				animate: true,
			});
		}
	}

	function openSettingsDialog() {
		isSettingsDialogOpen.value = true;
	}

	function closeSettingsDialog() {
		isSettingsDialogOpen.value = false;
	}

	function handleFieldCreationModeChange(creating: boolean) {
		isCreatingField.value = creating;
	}

	const omnibarClasses = computed(() => {
		const positionClasses = (() => {
			switch (omnibarStep.value) {
				case "selectField":
					return "left-6 top-6";
				case "pathGenerated":
					return "bottom-6 right-6";
				case "selectStartLocation":
				case "selectAbLine":
					return "bottom-6 left-1/2 transform -translate-x-1/2";
				default:
					return "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2";
			}
		})();

		const sizeClasses = (() => {
			if (omnibarStep.value === "selectField") {
				if (isCreatingField.value) {
					return "w-[400px] h-auto";
				}
				return "w-[400px] h-[calc(100vh-6rem)]";
			}
			if (
				isOmnibarExpanded.value ||
				omnibarStep.value === "pathPlanning" ||
				omnibarStep.value === "selectStartLocation" ||
				omnibarStep.value === "selectAbLine"
			) {
				return "w-[650px]";
			}
			return "w-[400px]";
		})();

		return [positionClasses, sizeClasses];
	});
</script>

<template>
	<MapLibrePolygon
		v-if="currentFieldBoundary"
		:polygon="currentFieldBoundary"
		:fill="fieldFill"
		:stroke="fieldStroke"
		:z-index="2"
	/>

	<MapLibrePolygon
		v-if="currentEstimatedCoverage"
		:polygon="currentEstimatedCoverage"
		:fill="estimatedCoverageFill"
		:stroke="estimatedCoverageStroke"
		:z-index="3"
	/>

	<MapLibrePolyline
		v-if="generatedPath"
		:polyline="generatedPath"
		:stroke="pathStroke"
		:symbol="pathSymbol"
		:z-index="4"
	/>

	<MapLibrePolygon
		v-for="(segment, index) in currentShortenedSegments"
		:key="`shortened-${index}`"
		:polygon="segment"
		:fill="shortenedSegmentFill"
		:stroke="shortenedSegmentStroke"
		:z-index="5"
	/>

	<MapLibreLabel
		v-if="currentStartPoint"
		:location="currentStartPoint"
	>
		<PinIcon
			class="rounded-full border-2 border-white shadow bg-green-600 text-white"
			small
		/>
	</MapLibreLabel>

	<MapLibreLabel
		v-if="currentEndPoint"
		:location="currentEndPoint"
	>
		<FinishIcon
			class="rounded-full border-2 border-white shadow bg-orange-600"
		/>
	</MapLibreLabel>

	<!-- Mission Preview Map Components -->
	<MapLibrePolygon
		v-if="missionPreviewCoverage"
		:polygon="missionPreviewCoverage"
		:stroke="coverageStroke"
		:fill="coverageFill"
		:z-index="6"
	/>
	<MapLibrePolyline
		v-if="missionPreviewDrivenPath"
		:polyline="missionPreviewDrivenPath"
		:stroke="drivenPathStroke"
		:z-index="7"
	/>
	<MapLibreMachineMarker
		v-if="missionPreviewMarker"
		:bearing-indicator="pointerAutoSvg"
		:location="missionPreviewMarker.location"
		:bearing="missionPreviewMarker.bearing"
		:icon="machineSvg"
	/>

	<div class="absolute inset-0 pointer-events-none">
		<div class="relative h-full">
			<PathResultsPanel
				v-if="
					planPathResults.length > 0 &&
					omnibarStep === 'pathGenerated'
				"
				class="pointer-events-auto"
				:plan-path-results="planPathResults"
				:selected-index="selectedPathIndex"
				:selected-field="selectedField"
				:selected-implement="selectedImplement"
				@change-path="handlePathChange"
				@modify-path="handleModifyPath"
				@save-path="handleSavePath"
				@clear-paths="resetOmnibar"
			/>

			<div
				class="absolute z-10 transition-all duration-300 pointer-events-auto"
				:class="omnibarClasses"
			>
				<div
					class="bg-white shadow-xl rounded-2xl overflow-hidden"
					:class="
						omnibarStep === 'selectField' && !isCreatingField
							? 'h-full'
							: ''
					"
				>
					<InitialPrompt
						v-if="omnibarStep === 'initial'"
						@click="startPlanning"
					/>

					<SelectImplement
						v-else-if="omnibarStep === 'selectImplement'"
						:all-implements="allImplements ?? []"
						:is-loading-implements="isLoadingImplements"
						@reset-omnibar="resetOmnibar"
						@select-implement="selectImplementFromOmnibar"
					/>

					<SelectField
						v-else-if="omnibarStep === 'selectField'"
						:all-fields="allFields ?? []"
						:is-loading-fields="isLoadingFields"
						:selected-field="selectedField!"
						:selected-implement="selectedImplement!"
						:is-creating-field="isCreatingField"
						@reset-omnibar="resetOmnibar"
						@handle-field-click="handleFieldClick"
						@field-creation-mode-changed="
							handleFieldCreationModeChange
						"
						@clear-field-selection="
							() => {
								selectedField = undefined;
							}
						"
						@back-to-select-implement="
							() => {
								selectedField = undefined;
								omnibarStep = 'selectImplement';
							}
						"
					/>

					<PathPlanningOptions
						v-else-if="
							omnibarStep === 'pathPlanning' ||
							omnibarStep === 'selectStartLocation' ||
							omnibarStep === 'selectAbLine'
						"
						ref="pathPlanningOptionsRef"
						:selected-field="selectedField!"
						:selected-implement="selectedImplement!"
						:planning-options="pathPlanningOptions"
						@back-to-select-field="
							() => {
								omnibarStep = 'selectField';
								scrollToSelectedField();
							}
						"
						@selecting-start-location="
							selecting =>
								(omnibarStep = selecting
									? 'selectStartLocation'
									: 'pathPlanning')
						"
						@selecting-ab-line="
							selecting =>
								(omnibarStep = selecting
									? 'selectAbLine'
									: 'pathPlanning')
						"
						@reset-omnibar="resetOmnibar"
						@paths-generated="handlePathsGenerated"
						@options-update="handlePlanningOptionsUpdate"
					/>

					<PathsGenerated
						v-else-if="omnibarStep === 'pathGenerated'"
						:selectedField="selectedField!"
						:selectedImplement="selectedImplement!"
						:plannedPath="
							planPathResults[selectedPathIndex]?.pathResult
								?.pathPoints || []
						"
						:targetSpeed="pathPlanningOptions.targetSpeed"
						:turningSpeed="pathPlanningOptions.turningSpeed"
						@mission-preview-update="handleMissionPreviewUpdate"
					/>
				</div>
			</div>

			<UserMenu
				class="pointer-events-auto"
				@open-settings="openSettingsDialog"
			/>
		</div>
	</div>

	<SettingsDialog
		v-if="isSettingsDialogOpen"
		@close="closeSettingsDialog"
		@reset="resetOmnibar"
	/>
</template>

<style>
	.maplibregl-ctrl-attrib-inner {
		font-size: 12px;
		line-height: 1.5;
		font-family:
			ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji",
			"Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
	}

	select:focus,
	textarea:focus,
	input:not([type="range"]):focus {
		outline: 2px solid #10b981 !important;
		outline-offset: 2px !important;
		box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
	}
</style>
